import {produce} from 'immer';
import type {
  ChatMessage,
  ChatStreamEvent,
  ChatToolCall,
} from '../../hooks/chat/types';

export type TurnPhase = 'idle' | 'sending' | 'streaming' | 'cancelling';

/**
 * What ties an answer settled under a client-minted id to the history record:
 * the question it replied to when known, the turn's completed messages (any
 * of which places the turn in a record), and its position among the turn's
 * assistant messages as the record counts them.
 */
export interface SettledAnswer {
  question: string | null;
  completed: ReadonlySet<string>;
  segment: number;
}

export interface TurnState {
  phase: TurnPhase;
  messages: ChatMessage[];
  streamUuid: string | null;
  chatId: string | null;
  pendingSendId: number | null;
  generation: number;
  /** Sessions this conversation has followed; a stale lookup cannot restart one. */
  streamUuids: ReadonlySet<string>;
  /**
   * A running session discovered while a send was in flight. The send, if
   * the server accepts it, supersedes it; if the send fails, it is followed.
   */
  deferredResume: string | null;
  /** Index of the first message the running turn added. */
  turnStart: number;
  /** Current turn ownership; settled partials retain this snapshot. */
  ownership: SettledAnswer;
  /** Answers settled here under minted ids, by id; carried across turns. */
  settled: ReadonlyMap<string, SettledAnswer>;
  nextSeq: number;
  parked: ReadonlyMap<number, ChatStreamEvent>;
  error: string | null;
  messageIndex: number;
  notifiedStart: boolean;
}
export type TurnAction =
  | {type: 'SEND'; sendId: number; user: ChatMessage}
  | {
      type: 'SESSION_ADOPTED';
      sendId: number;
      uuid: string;
      chatId?: string;
      /** Server id of the user message the send persisted. */
      messageId?: string;
    }
  | {type: 'RESUME'; uuid: string}
  | {type: 'START'}
  | {
      type: 'SERVER_EVENT';
      event: ChatStreamEvent;
      uuid?: string;
      ignore?: ChatStreamEvent['type'][];
    }
  | {type: 'CANCEL'}
  | {type: 'CANCEL_FAILED'; uuid: string; error: string}
  | {type: 'SEND_FAILED'; sendId: number; error: string}
  | {type: 'ABORT'; error?: string}
  | {type: 'HYDRATE'; messages: ChatMessage[]}
  | {type: 'ADD'; message: ChatMessage}
  | {type: 'REMOVE'; id: string}
  | {type: 'SWITCH'; chatId?: string; messages?: ChatMessage[]};

export function initialTurnState(
  messages: ChatMessage[] = [],
  chatId?: string,
): TurnState {
  return {
    phase: 'idle',
    messages,
    chatId: chatId ?? null,
    streamUuid: null,
    pendingSendId: null,
    generation: 0,
    streamUuids: new Set(),
    deferredResume: null,
    turnStart: messages.length,
    ownership: {question: null, completed: new Set(), segment: 0},
    settled: new Map(),
    nextSeq: 1,
    parked: new Map(),
    error: null,
    messageIndex: -1,
    notifiedStart: false,
  };
}

const STREAMING_ID = '__streaming__';
const PARTIAL_ID_PREFIX = 'partial-';
// Out-of-order events held for the cursor; past this the replay resends them.
const PARK_CAP = 200;
const TEMP_ID_PREFIX = 'temp-';

/**
 * Ids minted here for content the server has not returned under its own id:
 * the streaming placeholder, a partial answer settled by a cancellation, and
 * a user message whose send has not been acknowledged. Nothing keyed on a
 * server id (feedback, for one) can be done with them.
 */
export function isSyntheticMessageId(id: string): boolean {
  return (
    id === STREAMING_ID ||
    id.startsWith(PARTIAL_ID_PREFIX) ||
    id.startsWith(TEMP_ID_PREFIX)
  );
}

/**
 * Closes a message once its turn is over. Messages with nothing left open keep
 * their identity, so ending a turn does not re-render the whole transcript.
 */
function settleMessage(message: ChatMessage, id: string): ChatMessage {
  const hasOpenToolCall = message.toolCalls?.some((t) => t.streaming) ?? false;
  if (!message.streaming && !hasOpenToolCall) {
    return message;
  }
  return {
    ...message,
    ...(message.streaming ? {id, streaming: false} : {}),
    toolCalls: hasOpenToolCall
      ? message.toolCalls?.map((t) =>
          t.streaming ? {...t, streaming: false} : t,
        )
      : message.toolCalls,
  };
}

/**
 * Gives the unacknowledged user message its server id. A history record that
 * landed mid-send may already carry that id; then the placeholder just goes.
 */
function adoptUserMessage(
  messages: ChatMessage[],
  tempId: string,
  messageId?: string,
): ChatMessage[] {
  if (!messageId) {
    return messages;
  }
  if (messages.some((m) => m.id === messageId)) {
    return messages.filter((m) => m.id !== tempId);
  }
  return messages.map((m) => (m.id === tempId ? {...m, id: messageId} : m));
}

function settle(state: TurnState): TurnState {
  const settled = new Map(state.settled);
  const messages = state.messages
    .filter((m) => !m.streaming || m.content || m.toolCalls?.length)
    .map((m, index) => {
      const id = `${PARTIAL_ID_PREFIX}${state.generation}-${index}`;
      if (m.streaming) {
        settled.set(id, state.ownership);
      }
      return settleMessage(m, id);
    });
  return {
    ...state,
    phase: 'idle',
    error: null,
    streamUuid: null,
    pendingSendId: null,
    parked: new Map(),
    messages,
    settled,
  };
}

/** The stopped indicator hangs on the turn's last assistant message. */
function cancel(state: TurnState): TurnState {
  const settled = settle(state);
  const last = settled.messages.at(-1);
  if (last?.role === 'assistant' && settled.messages.length > state.turnStart) {
    return {
      ...settled,
      messages: [...settled.messages.slice(0, -1), {...last, cancelled: true}],
    };
  }
  const id = `${PARTIAL_ID_PREFIX}${state.generation}-stopped`;
  return {
    ...settled,
    messages: [
      ...settled.messages,
      {id, role: 'assistant', content: '', cancelled: true},
    ],
    settled: new Map(settled.settled).set(id, state.ownership),
  };
}

/**
 * The record's copy of a message this surface already holds. The record does
 * not know about cancellation, the runtime persists no tool calls, and Ruby
 * writes an empty row before the answer is streamed, so a record that is
 * empty where this surface has text is older than what was shown.
 */
function mergeMessage(
  message: ChatMessage,
  previous?: ChatMessage,
): ChatMessage {
  if (!previous) {
    return message;
  }
  return {
    ...message,
    ...(!message.content && previous.content
      ? {content: previous.content}
      : {}),
    ...(!message.toolCalls?.length && previous.toolCalls?.length
      ? {toolCalls: previous.toolCalls}
      : {}),
    ...(previous.cancelled ? {cancelled: true} : {}),
  };
}

function applyServerEvent(state: TurnState, event: ChatStreamEvent): TurnState {
  if (
    event.type === 'TOOL_CALL_EXECUTE' ||
    (event.type === 'TEXT_DELTA' && event.content.trim())
  ) {
    state = {...state, notifiedStart: true};
  }
  let messages = state.messages;
  switch (event.type) {
    case 'TEXT_DELTA':
      messages = updateStreamingMessage(
        ensureStreamingMessage(messages),
        (m) => ({
          ...m,
          content:
            (state.messageIndex === event.messageIndex
              ? (m.content ?? '')
              : '') + event.content,
        }),
      );
      return {...state, messages, messageIndex: event.messageIndex};
    case 'TOOL_CALL_EXECUTE':
    case 'TOOL_CALL_DELTA':
      if (isToolCallClaimed(messages, event.toolCallId)) {
        return state;
      }
      return {
        ...state,
        messages: updateStreamingMessage(
          ensureStreamingMessage(messages),
          (m) => ({
            ...m,
            toolCalls: upsertToolCall(m.toolCalls ?? [], event.toolCallId, {
              name: event.toolName,
              intent:
                event.type === 'TOOL_CALL_EXECUTE' ? event.intent : undefined,
            }),
          }),
        ),
      };
    case 'TOOL_CALL_RESULT':
      return {
        ...state,
        messages: messages.map((m) =>
          !m.toolCalls?.some((t) => t.toolCallId === event.toolCallId)
            ? m
            : {
                ...m,
                toolCalls: upsertToolCall(m.toolCalls, event.toolCallId, {
                  streaming: false,
                }),
              },
        ),
      };
    case 'MESSAGE_COMPLETE': {
      const last = messages.at(-1);
      const previous = last?.streaming
        ? last
        : messages.find((m) => m.id === event.message.id);
      const complete = mergeMessage(event.message, previous);
      messages = messages.filter((m) => !m.streaming && m.id !== complete.id);
      return {
        ...state,
        messages: [...messages, complete],
        messageIndex: -1,
        ownership: {
          ...state.ownership,
          completed: new Set(state.ownership.completed).add(complete.id),
          // The record lists only messages with content.
          segment:
            state.ownership.segment + (event.message.content == null ? 0 : 1),
        },
      };
    }
    case 'DONE':
      return settle(state);
    case 'CANCELLED':
      return cancel(state);
    case 'ERROR':
      return {
        ...settle({...state, messages: messages.filter((m) => !m.streaming)}),
        error: event.errorMessage,
      };
  }
}

function resume(state: TurnState, uuid: string): TurnState {
  return {
    ...initialTurnState(state.messages, state.chatId ?? undefined),
    phase: 'streaming',
    streamUuid: uuid,
    streamUuids: new Set(state.streamUuids).add(uuid),
    settled: state.settled,
    generation: state.generation + 1,
  };
}

export function reduceTurn(
  state: TurnState,
  action: TurnAction,
): {state: TurnState; events: ChatStreamEvent[]} {
  let next = state;
  const events: ChatStreamEvent[] = [];
  switch (action.type) {
    case 'SEND':
      if (state.phase !== 'idle') {
        break;
      }
      next = {
        ...initialTurnState(
          [...state.messages, action.user],
          state.chatId ?? undefined,
        ),
        phase: 'sending',
        pendingSendId: action.sendId,
        generation: state.generation + 1,
        streamUuids: state.streamUuids,
        settled: state.settled,
        turnStart: state.messages.length,
      };
      break;
    case 'SESSION_ADOPTED': {
      if (state.pendingSendId !== action.sendId) {
        break;
      }
      const {messageId} = action;
      const tempId = `${TEMP_ID_PREFIX}${action.sendId}`;
      next = {
        ...state,
        streamUuid: action.uuid,
        streamUuids: new Set(state.streamUuids).add(action.uuid),
        chatId: action.chatId ?? state.chatId,
        phase: state.phase === 'cancelling' ? 'cancelling' : 'streaming',
        pendingSendId: null,
        // The server took a new turn, so nothing else was running.
        deferredResume: null,
        ownership: {...state.ownership, question: messageId ?? null},
        messages: adoptUserMessage(state.messages, tempId, messageId),
      };
      break;
    }
    case 'RESUME':
      if (state.streamUuids.has(action.uuid)) {
        break;
      }
      // A send in flight is not interrupted; the session waits on its outcome.
      next =
        state.phase === 'idle'
          ? resume(state, action.uuid)
          : {...state, deferredResume: action.uuid};
      break;
    case 'START':
      next = {
        ...initialTurnState(state.messages, state.chatId ?? undefined),
        phase: 'streaming',
        streamUuids: state.streamUuids,
        settled: state.settled,
        generation: state.generation + 1,
      };
      break;
    case 'CANCEL':
      if (state.phase !== 'idle') {
        next = {...state, phase: 'cancelling', error: null};
      }
      break;
    case 'CANCEL_FAILED':
      if (state.streamUuid === action.uuid && state.phase === 'cancelling') {
        next = {...state, phase: 'streaming', error: action.error};
      }
      break;
    case 'SEND_FAILED': {
      if (state.pendingSendId !== action.sendId) {
        break;
      }
      const failed = {
        ...settle(state),
        messages: state.messages.filter(
          (m) => m.id !== `${TEMP_ID_PREFIX}${action.sendId}`,
        ),
      };
      const error = state.phase === 'cancelling' ? null : action.error;
      next = state.deferredResume
        ? {...resume(failed, state.deferredResume), error}
        : {...failed, error};
      break;
    }
    case 'ABORT':
      next = {
        ...settle({
          ...state,
          messages: state.messages.filter((m) => !m.streaming),
        }),
        error: action.error ?? state.error,
      };
      break;
    case 'HYDRATE': {
      const turnIds = new Set(
        state.messages.slice(state.turnStart).map((m) => m.id),
      );
      const live = state.phase !== 'idle';
      const recordIndex = new Map(action.messages.map((m, i) => [m.id, i]));
      const questionBefore = (index: number) => {
        for (let i = index - 1; i >= 0; i--) {
          if (action.messages[i].role === 'user') {
            return i;
          }
        }
        return -1;
      };
      // The record's assistant messages replying to the question at `at`.
      const replyAt = (at: number) => {
        const reply = action.messages.slice(at + 1);
        const end = reply.findIndex((m) => m.role === 'user');
        return reply
          .slice(0, end === -1 ? undefined : end)
          .filter((m) => m.role === 'assistant');
      };
      // Retain partials until a question or completed message proves their turn.
      // Unanchored resumed answers may duplicate history; guessing risks moving
      // their text, tools, and stopped marker onto another turn.
      const settled = new Map(state.settled);
      const recordCopyOf = (answer: ChatMessage) => {
        const known = settled.get(answer.id);
        if (!known) {
          return undefined;
        }
        let at = known.question ? recordIndex.get(known.question) : undefined;
        if (at === undefined) {
          const completedAt = action.messages.findIndex((m) =>
            known.completed.has(m.id),
          );
          if (completedAt !== -1) {
            at = questionBefore(completedAt);
          }
        }
        return at === undefined || at === -1
          ? undefined
          : replyAt(at)[known.segment];
      };
      const replacedBy = new Map<string, ChatMessage>();
      const dropped = new Set<string>();
      for (const m of state.messages) {
        if (!isSyntheticMessageId(m.id) || (live && turnIds.has(m.id))) {
          continue;
        }
        const copy = m.role === 'assistant' ? recordCopyOf(m) : undefined;
        if (copy) {
          replacedBy.set(copy.id, m);
        }
        if (copy || m.role !== 'assistant') {
          dropped.add(m.id);
        }
      }
      const canonical = action.messages.map((m) =>
        mergeMessage(
          m,
          replacedBy.get(m.id) ?? state.messages.find((p) => p.id === m.id),
        ),
      );
      const canonicalIds = new Set(canonical.map((m) => m.id));
      // What the record lacks keeps its place: after its nearest neighbour
      // still listed (or that neighbour's copy) and after the record's
      // messages this surface had not seen, which may include its own copy,
      // so that a later turn this surface knows stays after it.
      const known = new Set(state.messages.map((m) => m.id));
      const copyOf = new Map(
        [...replacedBy].map(([copyId, answer]) => [answer.id, copyId]),
      );
      const messages = [...canonical];
      state.messages.forEach((m, index) => {
        if (canonicalIds.has(m.id) || dropped.has(m.id)) {
          return;
        }
        let insertAt = messages.length;
        for (let i = index - 1; i >= 0; i--) {
          const id = copyOf.get(state.messages[i].id) ?? state.messages[i].id;
          const at = messages.findIndex((p) => p.id === id);
          if (at !== -1) {
            insertAt = at + 1;
            while (
              insertAt < messages.length &&
              !known.has(messages[insertAt].id)
            ) {
              insertAt++;
            }
            break;
          }
        }
        messages.splice(insertAt, 0, m);
      });
      const turnStart = messages.findIndex(
        (m) => turnIds.has(m.id) || replacedBy.has(m.id),
      );
      dropped.forEach((id) => settled.delete(id));
      next = {
        ...state,
        messages,
        settled,
        turnStart: turnStart === -1 ? messages.length : turnStart,
      };
      break;
    }
    case 'ADD':
      next = {...state, messages: [...state.messages, action.message]};
      break;
    case 'REMOVE':
      next = {
        ...state,
        messages: state.messages.filter((m) => m.id !== action.id),
      };
      break;
    case 'SWITCH':
      next = {
        ...initialTurnState(action.messages, action.chatId),
        generation: state.generation + 1,
      };
      break;
    case 'SERVER_EVENT': {
      if (action.uuid && action.uuid !== state.streamUuid) {
        break;
      }
      const event = action.event;
      if (state.phase === 'idle') {
        break;
      }
      if (event.seq >= 0 && event.seq < state.nextSeq) {
        break;
      }
      const parked = new Map(state.parked);
      // Past the cap, later events are dropped; the recovery replay resends them.
      if (
        event.seq >= 0 &&
        (parked.size < PARK_CAP || event.seq === state.nextSeq)
      ) {
        parked.set(event.seq, event);
      }
      next = {...state, parked};
      let current = event.seq < 0 ? event : parked.get(next.nextSeq);
      while (current) {
        parked.delete(current.seq);
        next = {
          ...next,
          nextSeq: current.seq < 0 ? next.nextSeq : current.seq + 1,
        };
        if (!action.ignore?.includes(current.type)) {
          next = applyServerEvent(next, current);
          events.push(current);
        }
        if (next.phase === 'idle' || current.seq < 0) {
          break;
        }
        current = parked.get(next.nextSeq);
      }
      break;
    }
  }
  return {state: next, events};
}

function isToolCallClaimed(
  messages: ChatMessage[],
  toolCallId: string,
): boolean {
  return messages.some(
    (msg) =>
      !msg.streaming &&
      msg.toolCalls?.some((tc) => tc.toolCallId === toolCallId),
  );
}

function ensureStreamingMessage(messages: ChatMessage[]): ChatMessage[] {
  const last = messages[messages.length - 1];
  if (last?.streaming) {
    return messages;
  }
  return [
    ...messages,
    {
      id: STREAMING_ID,
      role: 'assistant',
      content: '',
      toolCalls: [],
      streaming: true,
    },
  ];
}

function updateStreamingMessage(
  messages: ChatMessage[],
  updater: (msg: ChatMessage) => ChatMessage,
): ChatMessage[] {
  const last = messages[messages.length - 1];
  if (!last?.streaming) {
    return messages;
  }

  return [...messages.slice(0, -1), updater(last)];
}

function upsertToolCall(
  toolCalls: ChatToolCall[],
  toolCallId: string,
  update: Partial<Omit<ChatToolCall, 'toolCallId'>>,
): ChatToolCall[] {
  return produce(toolCalls, (draft) => {
    const existing = draft.find((tc) => tc.toolCallId === toolCallId);

    if (existing) {
      existing.name = update.name ?? existing.name;
      existing.intent = update.intent ?? existing.intent;
      existing.streaming = update.streaming ?? existing.streaming;
    } else {
      draft.push({
        toolCallId,
        name: update.name ?? '',
        intent: update.intent,
        streaming: update.streaming ?? true,
      });
    }
  });
}
